import React, { useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import DealerForm from '../components/DealerForm'

function ArrowLeft({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  )
}

function ArrowRight({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  )
}

export default function HomePage() {
  const carouselRef = useRef(null)

  const scroll = useCallback((direction) => {
    if (carouselRef.current) {
      const scrollAmount = carouselRef.current.offsetWidth * 0.8
      carouselRef.current.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' })
    }
  }, [])

  const categories = [
    { name: 'H-Series', slug: 'h-series', img: '/products/h-series-v2.png', desc: 'Портативная акустика' },
    { name: 'J-Series', slug: 'j-series', img: '/products/j-series-v2.png', desc: 'Активные системы' },
    { name: 'R-Series', slug: 'r-series', img: '/products/r-series-v2.png', desc: 'Профессиональная акустика' },
    { name: 'Сабвуферы', slug: 'сабвуферы', img: '/products/subwoofers-v2.png', desc: 'Низкочастотные системы' },
    { name: 'Микрофоны', slug: 'микрофоны', img: '/products/mic-280a-v2.png', desc: 'UHF системы' },
  ]

  return (
    <div className="min-h-screen bg-ss-white">
      <Helmet>
        <title>SwedenSound — Профессиональная акустика и микрофонные системы</title>
        <meta name="description" content="Профессиональная акустика и микрофонные системы для сцены, проката и инсталляций. Европейское звучание по лучшей цене. Доставка по РФ." />
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden flex items-center justify-center min-h-[80vh] pt-16">
        <div className="absolute inset-0" style={{ backgroundImage: `url('/backgrounds/bg-stage-venue.jpg?v=2')`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundColor: '#1a1a1a' }} />
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative mx-auto max-w-ss px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-6xl sm:text-7xl lg:text-8xl font-light tracking-tight text-white leading-[1.05] animate-fade-in-up">
            SwedenSound
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-xl text-white/70 font-light leading-relaxed animate-fade-in-up animation-delay-200">
            Профессиональная акустика и микрофонные системы для сцены, проката и инсталляций.
          </p>
          <p className="mx-auto mt-3 max-w-2xl text-lg text-white/50 animate-fade-in-up animation-delay-300">
            Европейское звучание по лучшей цене
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-400">
            <Link to="/catalog" className="inline-flex justify-center items-center rounded-full bg-white px-8 py-3 text-sm font-medium text-ss-dark hover:bg-ss-gray transition-all duration-300">
              Смотреть каталог
            </Link>
            <Link to="/about" className="inline-flex justify-center items-center rounded-full border border-white/30 px-8 py-3 text-sm font-medium text-white hover:bg-white/10 transition-all duration-300">
              О нас
            </Link>
          </div>
        </div>
      </section>

      {/* Categories Carousel */}
      <section className="py-20 sm:py-28 bg-ss-gray">
        <div className="mx-auto max-w-ss px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-light text-ss-text tracking-tight">
              Каталог продукции
            </h2>
            <p className="mt-3 text-ss-muted max-w-2xl mx-auto">
              Активная акустика, сабвуферы и микрофонные системы для профессионалов
            </p>
          </div>

          {/* Carousel with arrows */}
          <div className="relative">
            {/* Left arrow */}
            <button
              onClick={() => scroll(-1)}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white shadow-lg flex items-center justify-center text-gray-700 hover:bg-gray-900 hover:text-white transition-all hover:scale-110 active:scale-95 -ml-5 hidden md:flex"
              aria-label="Влево"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            {/* Right arrow */}
            <button
              onClick={() => scroll(1)}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white shadow-lg flex items-center justify-center text-gray-700 hover:bg-gray-900 hover:text-white transition-all hover:scale-110 active:scale-95 -mr-5 hidden md:flex"
              aria-label="Вправо"
            >
              <ArrowRight className="w-5 h-5" />
            </button>

            {/* Scrollable container */}
            <div
              ref={carouselRef}
              className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {categories.map((cat) => (
                <Link
                  key={cat.name}
                  to={`/catalog?category=${cat.slug}`}
                  className="group flex-shrink-0 w-[220px] sm:w-[260px] flex flex-col items-center rounded-xl bg-white p-6 border border-ss-border hover:shadow-md transition-all duration-500 hover:-translate-y-1"
                >
                  <div className="relative w-full aspect-square flex items-center justify-center mb-4">
                    <img
                      src={cat.img}
                      alt={cat.name}
                      className="max-h-32 w-auto object-contain transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                      draggable={false}
                    />
                  </div>
                  <h3 className="text-base font-medium text-ss-text text-center">{cat.name}</h3>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/catalog"
              className="inline-flex items-center gap-2 rounded-full border border-ss-text px-8 py-3 text-sm font-medium text-ss-text hover:bg-ss-dark hover:text-white hover:border-ss-dark transition-all duration-300"
            >
              Все товары
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Dealer CTA */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="mx-auto max-w-ss px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-light text-ss-text tracking-tight mb-4">
                Станьте дилером SwedenSound
              </h2>
              <p className="text-ss-muted leading-relaxed max-w-md">
                Получите доступ к дилерским ценам, технической поддержке и маркетинговым материалам.
                Работаем с музыкальными магазинами, инсталляционными компаниями и прокатами.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-ss-text">
                <li className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-ss-dark text-white text-[10px] font-bold">1</span>
                  Специальные дилерские цены
                </li>
                <li className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-ss-dark text-white text-[10px] font-bold">2</span>
                  Техническая поддержка
                </li>
                <li className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-ss-dark text-white text-[10px] font-bold">3</span>
                  Маркетинговые материалы
                </li>
              </ul>
            </div>
            <div className="rounded-2xl bg-ss-gray border border-ss-border p-6 sm:p-8">
              <h3 className="text-lg font-medium text-ss-text mb-1">Оставить заявку</h3>
              <p className="text-sm text-ss-muted mb-6">
                Заполните форму — мы рассмотрим заявку в течение 2 рабочих дней.
              </p>
              <div id="dealer-form">
                <DealerForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
